/**
 * Geometric Shatter - 几何破碎重组效果
 *
 * 设计目标：
 * - 文本「BREAKING NEWS」先完整显示，再破碎爆炸，最后回归重组。
 * - 使用几何碎片（三角形/四边形）实现硬朗、新闻感的视觉冲击。
 * - 保持可重复渲染：随机结果完全确定性，不会帧间抖动失控。
 *
 * 技术实现：
 * - SVG + clipPath：先绘制文本轮廓区域，再将碎片裁切到文本内部。
 * - useMemo：碎片几何和基础运动参数只计算一次，避免每帧重复构造大数组。
 * - Spring：爆炸与重组阶段均使用 damping: 200 的平滑弹性。
 * - interpolate：控制位移、旋转、透明度和辅助光效强度。
 */

import React, { useMemo } from 'react';
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';

interface Point {
  x: number;
  y: number;
}

interface FragmentShape {
  id: number;
  points: Point[];
  centerX: number;
  centerY: number;
  explodeAngle: number;
  explodeDistance: number;
  rotateMaxDeg: number;
  delayFrame: number;
  color: string;
}

const BACKGROUND_COLOR = '#08090c';
const PRIMARY_COLOR = '#f59e0b';
const SECONDARY_COLOR = '#06b6d4';
const LABEL_TEXT = 'BREAKING NEWS';

const deterministicRandom = (seed: number): number => {
  const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453123;
  return value - Math.floor(value);
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value));
};

const polygonToPath = (points: Point[]): string => {
  if (points.length === 0) {
    return '';
  }

  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ')
    .concat(' Z');
};

const getPolygonCenter = (points: Point[]): Point => {
  const sum = points.reduce(
    (acc, point) => {
      return { x: acc.x + point.x, y: acc.y + point.y };
    },
    { x: 0, y: 0 }
  );

  return { x: sum.x / points.length, y: sum.y / points.length };
};

export const GeometricShatter: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // 文本布局：使用 clipPath 将碎片裁切进文字轮廓（浏览器会将文本按矢量轮廓裁切）。
  const textLayout = useMemo(() => {
    const textWidth = width * 0.8;
    const textHeight = height * 0.18;
    const x = (width - textWidth) / 2;
    const y = (height - textHeight) / 2;

    return {
      x,
      y,
      textWidth,
      textHeight,
      fontSize: Math.floor(Math.min(width * 0.14, 128)),
      letterSpacing: Math.max(3, Math.floor(width * 0.004)),
    };
  }, [height, width]);

  // 预计算几何碎片：固定网格 + 抖动顶点，混合三角形与四边形。
  const fragments = useMemo<FragmentShape[]>(() => {
    const columns = 14;
    const rows = 6;
    const cellWidth = textLayout.textWidth / columns;
    const cellHeight = textLayout.textHeight / rows;

    const result: FragmentShape[] = [];
    let nextId = 0;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const x0 = textLayout.x + col * cellWidth;
        const y0 = textLayout.y + row * cellHeight;
        const x1 = x0 + cellWidth;
        const y1 = y0 + cellHeight;

        const jitter = Math.min(cellWidth, cellHeight) * 0.16;
        const jittered = (seed: number, base: Point): Point => {
          return {
            x: base.x + (deterministicRandom(seed * 10 + 1) - 0.5) * jitter,
            y: base.y + (deterministicRandom(seed * 10 + 2) - 0.5) * jitter,
          };
        };

        const p0 = jittered(nextId + 11, { x: x0, y: y0 });
        const p1 = jittered(nextId + 13, { x: x1, y: y0 });
        const p2 = jittered(nextId + 17, { x: x1, y: y1 });
        const p3 = jittered(nextId + 19, { x: x0, y: y1 });

        const diagonalMode = deterministicRandom(nextId + 23);
        const colorMode = deterministicRandom(nextId + 31);
        const explodeSeed = deterministicRandom(nextId + 41);

        const pushFragment = (points: Point[], localId: number) => {
          const center = getPolygonCenter(points);
          const centerToOriginAngle = Math.atan2(center.y - height / 2, center.x - width / 2);
          const angleOffset = (deterministicRandom(localId + 53) - 0.5) * 0.8;
          const explodeAngle = centerToOriginAngle + angleOffset;

          result.push({
            id: localId,
            points,
            centerX: center.x,
            centerY: center.y,
            explodeAngle,
            explodeDistance: 90 + explodeSeed * 380,
            rotateMaxDeg: (deterministicRandom(localId + 59) - 0.5) * 300,
            delayFrame: Math.floor(deterministicRandom(localId + 61) * 10),
            color: colorMode > 0.46 ? PRIMARY_COLOR : SECONDARY_COLOR,
          });
        };

        if (diagonalMode < 0.55) {
          // 生成两片三角形，覆盖同一网格单元。
          pushFragment([p0, p1, p2], nextId++);
          pushFragment([p0, p2, p3], nextId++);
        } else {
          // 生成四边形碎片。
          pushFragment([p0, p1, p2, p3], nextId++);
        }
      }
    }

    return result;
  }, [height, textLayout.textHeight, textLayout.textWidth, textLayout.x, textLayout.y, width]);

  // 时间轴控制：0-60 完整展示；60-120 爆炸；120-180 重组。
  const explosionWindow = interpolate(frame, [60, 120], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const reassembleWindow = interpolate(frame, [120, 180], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const explosionSpring = spring({
    frame: Math.max(0, frame - 60),
    fps,
    config: {
      damping: 200,
      stiffness: 120,
      mass: 0.9,
    },
  });

  const reassembleSpring = spring({
    frame: Math.max(0, frame - 120),
    fps,
    config: {
      damping: 200,
      stiffness: 110,
      mass: 0.9,
    },
  });

  const explosionProgress = clamp(Math.min(explosionWindow, explosionSpring), 0, 1);
  const reassembleProgress = clamp(Math.min(reassembleWindow, reassembleSpring), 0, 1);
  const travelProgress = clamp(explosionProgress * (1 - reassembleProgress), 0, 1);

  const globalAlpha = interpolate(frame, [0, 60, 90, 120, 150, 180], [1, 1, 0.92, 0.82, 0.9, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill style={{ backgroundColor: BACKGROUND_COLOR }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <clipPath id="geometric-shatter-text-clip">
            <text
              x={width / 2}
              y={height / 2}
              fill="white"
              textAnchor="middle"
              dominantBaseline="middle"
              fontWeight={900}
              fontFamily="Arial Black, Impact, sans-serif"
              fontSize={textLayout.fontSize}
              letterSpacing={textLayout.letterSpacing}
            >
              {LABEL_TEXT}
            </text>
          </clipPath>

          <filter id="fragment-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="5" result="blurred" />
            <feMerge>
              <feMergeNode in="blurred" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 背景辅助光带：提供爆炸阶段的动势，不影响主体可读性。 */}
        <g opacity={interpolate(travelProgress, [0, 1], [0.08, 0.24])}>
          <circle cx={width * 0.25} cy={height * 0.42} r={220} fill={SECONDARY_COLOR} opacity={0.25} />
          <circle cx={width * 0.72} cy={height * 0.58} r={280} fill={PRIMARY_COLOR} opacity={0.2} />
        </g>

        <g clipPath="url(#geometric-shatter-text-clip)">
          {fragments.map((fragment) => {
            // 基于帧+索引的确定性微扰，避免纯静态导致的“死板感”。
            const frameNoise = deterministicRandom(fragment.id * 101 + frame * 31);
            const delayedTravel = clamp(
              interpolate(travelProgress, [0, 1], [-fragment.delayFrame / 20, 1]),
              0,
              1
            );

            const offsetX = Math.cos(fragment.explodeAngle) * fragment.explodeDistance * delayedTravel;
            const offsetY = Math.sin(fragment.explodeAngle) * fragment.explodeDistance * delayedTravel;
            const rotationDeg = interpolate(
              delayedTravel,
              [0, 1],
              [0, fragment.rotateMaxDeg + (frameNoise - 0.5) * 6]
            );
            const fragmentAlpha = interpolate(
              delayedTravel,
              [0, 0.25, 0.7, 1],
              [1, 0.98, 0.78, 0.65],
              {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              }
            );

            return (
              <path
                key={fragment.id}
                d={polygonToPath(fragment.points)}
                fill={fragment.color}
                fillOpacity={fragmentAlpha * globalAlpha}
                stroke={frameNoise > 0.5 ? PRIMARY_COLOR : SECONDARY_COLOR}
                strokeWidth={0.9}
                strokeOpacity={0.35}
                transform={`translate(${offsetX.toFixed(2)} ${offsetY.toFixed(2)}) rotate(${rotationDeg.toFixed(2)} ${fragment.centerX.toFixed(2)} ${fragment.centerY.toFixed(2)})`}
                filter="url(#fragment-glow)"
              />
            );
          })}
        </g>

        {/* 文本轮廓增强：保持 0-60 帧的完整状态，并在重组后强化边缘。 */}
        <text
          x={width / 2}
          y={height / 2}
          fill="transparent"
          stroke={PRIMARY_COLOR}
          strokeWidth={1.4}
          strokeOpacity={interpolate(frame, [0, 60, 120, 180], [0.65, 0.65, 0.28, 0.72], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}
          textAnchor="middle"
          dominantBaseline="middle"
          fontWeight={900}
          fontFamily="Arial Black, Impact, sans-serif"
          fontSize={textLayout.fontSize}
          letterSpacing={textLayout.letterSpacing}
        >
          {LABEL_TEXT}
        </text>
      </svg>
    </AbsoluteFill>
  );
};
